import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuItem from '@mui/material/MenuItem';
import OvSelect from '../atoms/OvSelect';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import breakpoints from '../../../design-system/breakpoints';
import DateUtils from '../../../common/utils/services/date-utils';
import { UserCycle } from '../../../common/model/dto/user-cycle/user-cycle';
import { MIN_CYCLE_SCAN_DAYS } from '../../../common/utils/services/hormone-trends-utils';

interface CycleSelectorProps {
  cycles: UserCycle[];
  selectedCycleId: string;
  onCycleSelect: (cycle: UserCycle) => void;
  isLoading?: boolean;
}

type CycleOvulationStatus = 'ovulatory' | 'anovulatory' | 'insufficient';

const getCycleOvulationStatus = (
  cycle: UserCycle
): CycleOvulationStatus | null => {
  if (
    cycle.scan_day_count !== undefined &&
    cycle.scan_day_count < MIN_CYCLE_SCAN_DAYS
  ) {
    return 'insufficient';
  }

  if (cycle.is_ovulatory_cycle === undefined) {
    return null;
  }

  return cycle.is_ovulatory_cycle ? 'ovulatory' : 'anovulatory';
};

const CycleSelector: FC<CycleSelectorProps> = ({
  cycles,
  selectedCycleId,
  onCycleSelect,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  return (
    <CycleSelectorWrapper>
      <StyledOvSelect
        value={selectedCycleId}
        onChange={(e) => {
          const cycle = cycles.find((c) => c.id === e.target.value);
          if (cycle) onCycleSelect(cycle);
        }}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return (
              <PlaceholderText>
                {t('pdfReports.selectCycle', 'Select a cycle')}
              </PlaceholderText>
            );
          }
          const selectedCycle = cycles.find((c) => c.id === selected);
          return selectedCycle ? (
            <SelectedCycleView>
              <SyncIcon fontSize="small" color="action" />
              {DateUtils.formatDateRange(
                selectedCycle.start_day,
                selectedCycle.end_day
              )}
            </SelectedCycleView>
          ) : null;
        }}
      >
        <MenuItem value="" disabled style={{ display: 'none' }}>
          {t('pdfReports.selectCycle', 'Select a cycle')}
        </MenuItem>
        {isLoading ? (
          <MenuItem disabled>{t('common.loading', 'Loading...')}</MenuItem>
        ) : cycles.length === 0 ? (
          <MenuItem disabled>
            {t('pdfReports.noCyclesFound', 'No cycles found')}
          </MenuItem>
        ) : (
          cycles.map((cycle) => {
            const ovulationStatus = getCycleOvulationStatus(cycle);

            return (
              <StyledMenuItem key={cycle.id} value={cycle.id}>
                <CycleItemContent>
                  <CycleDateRange>
                    {DateUtils.formatDateRange(cycle.start_day, cycle.end_day)}
                  </CycleDateRange>
                  <CycleDetailsRow>
                    <CycleDuration>
                      {`${cycle.cycle_length} ${t('common.days', 'days')}`}
                    </CycleDuration>
                    {ovulationStatus && (
                      <CycleStatus $status={ovulationStatus}>
                        {ovulationStatus === 'ovulatory' ? (
                          <CheckCircleIcon fontSize="inherit" />
                        ) : (
                          <InfoOutlinedIcon fontSize="inherit" />
                        )}
                        {ovulationStatus === 'ovulatory' &&
                          t('userCycles.ovulatory', 'Ovulatory')}
                        {ovulationStatus === 'anovulatory' &&
                          t('userCycles.anovulatory', 'Anovulatory')}
                        {ovulationStatus === 'insufficient' &&
                          t('userCycles.insufficientData', 'Insufficient data')}
                      </CycleStatus>
                    )}
                  </CycleDetailsRow>
                </CycleItemContent>
              </StyledMenuItem>
            );
          })
        )}
      </StyledOvSelect>
    </CycleSelectorWrapper>
  );
};

export default CycleSelector;

const CycleSelectorWrapper = styled.div`
  flex-grow: 1;
  margin-right: 2rem;
  padding: 0.5rem 0.5rem 0.5rem 1rem;

  @media (max-width: ${breakpoints.md}) {
    margin-right: 0;
    padding: 0.5rem 1rem;
  }
`;

const StyledOvSelect = styled(OvSelect)`
  && {
    min-width: 320px;
    background-color: ${Colours.OV_WHITE};
    border-radius: ${Variables.borderRadius.LARGE};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    @media (max-width: ${breakpoints.md}) {
      min-width: 100%;
    }

    .MuiSelect-select {
      padding: 0.75rem 1rem;
      border-radius: ${Variables.borderRadius.LARGE};
    }

    fieldset {
      border-color: rgba(0, 0, 0, 0.1);
    }

    &:hover fieldset {
      border-color: ${Colours.OV_BASE_LIGHT};
    }
  }
`;

const PlaceholderText = styled.span`
  color: ${Colours.MEDIUM_GRAY_BLUE};
  font-weight: 500;
`;

const SelectedCycleView = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${Colours.OV_BASE};
  font-weight: 600;
`;

const StyledMenuItem = styled(MenuItem)`
  && {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);

    &:last-child {
      border-bottom: none;
    }

    &.Mui-selected {
      background-color: ${Colours.OV_LIGHT_BLUE};
      &:hover {
        background-color: ${Colours.OV_LIGHT_BLUE_HOVER};
        color: ${Colours.WHITE};
      }
    }
  }
`;

const CycleItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
`;

const CycleDateRange = styled.span`
  font-weight: 600;
  color: ${Colours.OV_BASE};
  font-size: 0.95rem;
`;

const CycleDetailsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CycleDuration = styled.span`
  font-size: 0.8rem;
  color: ${Colours.OV_CHARCOAL};
  background-color: ${Colours.LIGHT_GRAY_BLUE};
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
`;

const CycleStatus = styled.span<{ $status: CycleOvulationStatus }>`
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ $status }) =>
    $status === 'ovulatory' ? Colours.FULLSCRIPT_GREEN : Colours.OV_CHARCOAL};
  background-color: ${({ $status }) =>
    $status === 'ovulatory' ? Colours.OV_LIGHT_GRAY : Colours.LIGHT_GRAY_BLUE};
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
`;
